import { maybeWebContext } from "../search";
// chat/brain.ts — the chat "brain" for Oriora Focus. SERVER-FIRST: one billed call to the generic
// chat route (POST /api/apps/solo_focus_log/chat) sends the user's message + the CHAT_ACTIONS
// catalog + a SMALL context (default length + recent-session start times/durations — reflection
// text never leaves the device here) and gets back ONE { action, params, reply }. The server
// injects the CURRENT DATE, so relative dates resolve there; params come back LOOSE, so toAction()
// below coerces every type and resolves references ("last", a date from the context list) to local
// row ids before anything executes — the model never touches the store directly.
//
// The original regex router (routeMessage) is kept FULLY INTACT below as the free offline /
// signed-out fallback: if the server call fails for ANY reason (no session, no funds, no network),
// askBrain falls back to it, so the chat always answers.
//
// HITL discipline is unchanged: delete_session is still held by the UI for an explicit Confirm tap.

import { BrainError, brainCall } from "../brain";
import { store, type FocusSession, type Goal } from "../store";
import type { ChatAction, FilterRow } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; the executor supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); askBrain assembles the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope`. Focus is a neutral productivity domain (no health/
// finance line) — this just keeps the reply channel in-scope + observational.
const SCOPE =
  "This app is a private focus/deep-work log: log focus sessions, delete/search them, set the default " +
  "length, and give an OBSERVATIONAL weekly summary of the user's OWN logged sessions; back up to their " +
  "own cloud. Describe their logged data; don't diagnose, prescribe, or give productivity/health advice.";

const HELP =
  'I can log a focus session, delete one, search your history, set your default length, summarize your week, ' +
  'draft an AI weekly plan, repeat your last session, track a goal, or find ideas related to one — just tell me. Try:\n' +
  '• "log a 25 minute focus session"\n' +
  '• "show last 7 days" · "show completed sessions"\n' +
  '• "set default focus to 50 minutes"\n' +
  '• "summarize my week"\n' +
  '• "draft me a focus plan" · "repeat my last session"\n' +
  '• "add a goal to learn Spanish" · "give me ideas for my Spanish goal"\n' +
  '• "delete my last session"';

/** "Draft me a focus plan" — checked before the generic filter/summarize matches so it opens the
 *  real AI plan flow instead of being misread as a search. Mirrors Workout Tracker's isDraftProgramRequest. */
function isDraftPlanRequest(qRaw: string): boolean {
  const q = ` ${qRaw.toLowerCase().replace(/[^a-z0-9\s']/g, " ").replace(/\s+/g, " ").trim()} `;
  return /\b(draft|design|build|plan|create|redo|regenerate|change|adjust|redraft)\s+(me\s+)?(a\s+|my\s+)?(new\s+)?(weekly\s+)?(focus\s+)?(plan|schedule)\b/.test(q);
}

/** "Repeat my last session" — deterministic, no AI call, pre-fills the timer. Mirrors Workout
 *  Tracker's isRepeatWorkoutRequest. */
function isRepeatSessionRequest(qRaw: string): boolean {
  const q = ` ${qRaw.toLowerCase().replace(/[^a-z0-9\s']/g, " ").replace(/\s+/g, " ").trim()} `;
  return /\b(repeat|redo)\s+(my\s+|the\s+)?(last\s+|previous\s+)?session\b/.test(q)
    || /\bdo\s+(it|that|this)\s+again\b/.test(q)
    || /\bsame\s+(session\s+)?as\s+(last\s+time|before|yesterday)\b/.test(q);
}

/** "Add a goal to learn Spanish" — best-effort local extraction (server path handles this better;
 *  this is only the offline/signed-out fallback). Returns the title, or null if not a goal-add ask. */
function matchCreateGoal(text: string): string | null {
  const m = text.match(/\b(?:add|create|set|start)\s+(?:a\s+|my\s+|new\s+)*goal\s*(?:to|:|for|of)?\s*(.+)$/i);
  return m ? m[1].trim().replace(/[.!]+$/, "") : null;
}

/** "Give me ideas for my Spanish goal" / "what's cool related to X" — best-effort local extraction. */
function matchGoalIdeas(text: string): string | null {
  let m = text.match(/\bideas?\s+(?:for|about|related\s+to)\s+(?:my\s+)?(.+?)\s*(?:goal)?[.!?]*$/i);
  if (m) return m[1].trim();
  m = text.match(/\bwhat'?s?\s+cool\s+(?:related\s+to|about)\s+(.+?)[.!?]*$/i);
  return m ? m[1].trim() : null;
}

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

const fmtWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16).replace("T", " ");
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

// ── SERVER-FIRST PATH ────────────────────────────────────────────────────────────

// The action catalog the server model chooses from. It MUST mirror ChatAction (chat/actions.ts)
// exactly — never declare an action the app can't execute.
// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.focus-log.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  {
    name: "add_session",
    description: "Log a finished focus session (recorded as completed, ending now).",
    params: {
      duration_min: "how long the session lasted — integer minutes, 1-600",
      reflection: "optional short reflection/note to attach, plain text; omit if none was given",
    },
  },
  {
    name: "delete_session",
    description: "Delete ONE existing focus session (the user confirms in the UI before it runs).",
    params: {
      which:
        "'last' for the most recent session, OR the target session's start date/time copied from the " +
        "context list (YYYY-MM-DD or YYYY-MM-DDTHH:MM), OR its duration in minutes if that is unique",
    },
  },
  {
    name: "filter",
    description: "List/search the user's focus-session history.",
    params: {
      days: "optional lookback window — integer days (7 = last week, 30 = last month); omit for the whole history",
      completed_only: "optional boolean — true to show only sessions that ran to completion",
      label: "short human label for the result set, e.g. 'last 7 days, completed'",
    },
  },
  {
    name: "set_default",
    description: "Set the default focus length the timer starts with.",
    params: { minutes: "the new default — integer minutes, 1-600" },
  },
  {
    name: "summarize",
    description: "Generate the AI summary of the last 7 days of focus work.",
    params: {},
  },
  { name: "start_focus", description: "START the focus timer NOW for the given number of minutes. Use for 'start a session', 'start a 25 minute block', 'focus for an hour' (60). This RUNS the timer — do not use for logging a finished session (that's add_session).", params: { duration_min: "whole minutes, e.g. 25" } },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE session log instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  { name: "open_plan_flow", description: "Open the AI-drafted weekly focus plan: designs a real week of sessions from the user's own patterns for them to review/edit before anything saves. Use for 'draft/design/create/build/plan a (new) (weekly) focus plan or schedule'.", params: {} },
  { name: "repeat_session", description: "Pre-fill the timer with the SAME duration as the user's most recent focus session, ready for them to press Start. Use for 'repeat my last session', 'do that again', 'same as last time'.", params: {} },
  { name: "create_goal", description: "Add a new personal goal — a real project/interest the user is working toward (e.g. 'Learn Spanish', 'Finish my novel'), not a minutes target. Use for 'add a goal to...', 'I want to work on...', 'track my ... goal'.", params: { title: "the goal, short — e.g. 'Learn Spanish'" } },
  { name: "goal_ideas", description: "DISCOVERY, not advice — runs a real web search and surfaces concrete, cited things related to a goal (resources, communities, what people pursuing the same goal actually do). Never prescriptive. Use for 'give me ideas for my X goal', 'what's cool related to X', 'what do people who do X also do'.", params: { goal_title: "the goal to find ideas for" } },
  { name: "delete_goal", description: "Delete ONE of the user's goals by its title (the app asks the user to confirm first).", params: { title: "REQUIRED — which goal, its title as the user wrote it" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerChat {
  actions?: unknown[];   // the shared brain returns an ARRAY (G17); we fall back to single `action`.
  action?: unknown;
  params?: unknown;
  reply?: unknown;
}

// ONE billed call: message + action catalog + a SMALL app-data context + the recent turns.
// Reuses the app's own brainCall (already scoped to /api/apps/solo_focus_log/) with endpoint 'chat'.
async function serverChat(prior: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<ServerChat> {
  const [settings, all] = await Promise.all([store.getSettings(), store.listSessions(50000)]);
  const recent = [...all].sort((a, b) => (a.started_at < b.started_at ? 1 : -1)).slice(0, 10);
  const lines = recent.map(
    (s) => `- started ${s.started_at.slice(0, 16)} · ${s.duration_min} min · ${s.completed_at ? "completed" : "stopped early"}`,
  );
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Default focus length: ${settings.default_duration_min} min. Sessions on record: ${all.length}.` +
    (lines.length ? `\nMost recent sessions (newest first):\n${lines.join("\n")}` : "\nNo sessions logged yet.")) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const history = prior
    .filter((t) => t.content.trim().length > 0)
    .slice(-7)
    .map((t) => ({ role: t.role, content: t.content }));
  return brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history, scope: SCOPE }, onChunk);
}

// Defensive coercion — the server returns params LOOSE (strings for numbers, extra text, etc.).
const asStr = (v: unknown): string =>
  typeof v === "string" ? v.trim() : typeof v === "number" && Number.isFinite(v) ? String(v) : "";
const asInt = (v: unknown, min: number, max: number): number | null => {
  const n = typeof v === "number" ? v : parseFloat(asStr(v).replace(/[^0-9.\-]/g, ""));
  if (!Number.isFinite(n)) return null;
  return Math.max(min, Math.min(max, Math.round(n)));
};
const asBool = (v: unknown): boolean =>
  v === true || v === 1 || (typeof v === "string" && /^(true|yes|1)$/i.test(v.trim()));

// Resolve the model's loose reference to EXACTLY one local session, or return a helpful message
// (single-match discipline — never guess between candidates).
// Exported so the composer's photo turn files onto the SAME session the typed commands would pick
// (one resolution rule, not two). Everything else about this function is unchanged.
export async function resolveSession(which: string): Promise<FocusSession | string> {
  const all = [...(await store.listSessions(50000))].sort((a, b) => (a.started_at < b.started_at ? 1 : -1));
  if (all.length === 0) return "There are no sessions to delete.";
  const w = which.trim().toLowerCase();
  if (!w || /^(?:the\s+)?(?:last|latest|newest|most\s+recent|recent|previous)(?:\s+(?:one|session))?$/.test(w)) return all[0];

  let hits: FocusSession[] = [];
  const iso = which.match(/\d{4}-\d{2}-\d{2}(?:[ tT]\d{2}:\d{2})?/);
  if (iso) {
    const prefix = iso[0].replace(" ", "T").toUpperCase();
    hits = all.filter((s) => s.started_at.toUpperCase().startsWith(prefix));
  } else {
    const mins = asInt(which, 1, 600);
    if (mins !== null) hits = all.filter((s) => s.duration_min === mins);
  }
  if (hits.length === 1) return hits[0];
  if (hits.length > 1) {
    return (
      `I found ${hits.length} sessions matching "${which}" — be more specific:\n` +
      hits.slice(0, 5).map((s) => `• ${fmtWhen(s.started_at)} — ${s.duration_min} min`).join("\n")
    );
  }
  return `I couldn't find a session matching "${which}".`;
}

// Resolve a goal reference to EXACTLY one goal by title, or a helpful message (0/1/many).
async function resolveGoal(term: string): Promise<{ goal?: Goal; problem?: string }> {
  if (!term) return { problem: "Which goal do you mean? Give me its title." };
  const goals = await store.listGoals();
  if (goals.length === 0) return { problem: "You have no goals to delete yet." };
  const q = term.trim().toLowerCase();
  const exact = goals.filter((g) => g.title.toLowerCase() === q);
  const hits = exact.length ? exact : goals.filter((g) => g.title.toLowerCase().includes(q));
  if (hits.length === 0) return { problem: `I couldn't find a goal matching "${term}".` };
  if (hits.length > 1) return { problem: `I found ${hits.length} goals matching "${term}" — be more specific:\n${hits.slice(0, 5).map((g) => `• ${g.title}`).join("\n")}` };
  return { goal: hits[0] };
}

// Coerce the server's loose { action, params, reply } into ONE well-typed ChatAction. Anything
// missing/unresolvable → the 'none' action with ONE helpful message.
export async function toAction(r: ServerChat): Promise<One> {
  const name = asStr(r.action);
  const p: Record<string, unknown> =

    r.params && typeof r.params === "object" && !Array.isArray(r.params) ? (r.params as Record<string, unknown>) : {};
  const reply = asStr(r.reply);
  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: reply, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: reply, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: reply, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: reply, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: reply, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }

  switch (name) {
    case "add_session": {
      const mins = asInt(p.duration_min, 1, 600);
      if (mins === null) return none('How long was it? Try "log a 25 minute focus session".');
      const reflection = asStr(p.reflection) || null;
      return { reply, action: { kind: "add_session", duration_min: mins, reflection } };
    }
    case "delete_session": {
      const hit = await resolveSession(asStr(p.which));
      if (typeof hit === "string") return none(hit);
      return {
        reply,
        action: { kind: "delete_session", id: hit.id, label: `the ${hit.duration_min}-minute session from ${fmtWhen(hit.started_at)}` },
      };
    }
    case "filter": {
      const days = asInt(p.days, 1, 3650);
      const completedOnly = asBool(p.completed_only ?? p.completedOnly);
      const label =
        asStr(p.label) ||
        (days ? `last ${days} days${completedOnly ? ", completed" : ""}` : completedOnly ? "completed" : "recent sessions");
      return { reply, action: { kind: "filter", days: days ?? undefined, completedOnly, label } };
    }
    case "set_default": {
      const mins = asInt(p.minutes, 1, 600);
      if (mins === null) return none('What should the default be? Try "set default focus to 25 minutes".');
      return { reply, action: { kind: "set_default", minutes: mins } };
    }
    case "summarize":
      return { reply, action: { kind: "summarize" } };
    case "none":
    case "backup_save":
      return { reply, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) || asStr(p.contentKind);
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) || asStr(p.content) || undefined, destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(reply || "What should I remember?");
      return { reply, action: { kind: "remember", instruction } };
    }
    case "open_plan_flow":
      return { reply, action: { kind: "open_plan_flow" } };
    case "start_focus": {
      const mins = parseFloat(String(p.duration_min ?? ""));
      if (!Number.isFinite(mins) || mins <= 0) return none(reply || "How many minutes should the session be?");
      return { reply, action: { kind: "start_focus", duration_min: Math.round(mins) } };
    }
    case "repeat_session":
      return { reply, action: { kind: "repeat_session" } };
    case "create_goal": {
      const title = asStr(p.title);
      if (!title) return none(reply || 'What should the goal be called? Try "add a goal to learn Spanish".');
      return { reply, action: { kind: "create_goal", title } };
    }
    case "goal_ideas": {
      const goalTitle = asStr(p.goal_title);
      if (!goalTitle) return none(reply || "Which goal do you want ideas for?");
      return { reply, action: { kind: "goal_ideas", goal_title: goalTitle } };
    }
    case "delete_goal": {
      const term = asStr(p.title) || asStr(p.goal_title);
      const { goal, problem } = await resolveGoal(term);
      if (!goal) return none(problem!);
      return { reply, action: { kind: "delete_goal", id: goal.id, label: `"${goal.title}" goal` } };
    }
    default:
      // The server's reply IS the answer for chit-chat/unsupported asks; HELP when it sent nothing.
      return none(reply || HELP);
  }
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  let idx = -1;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].role === "user") { idx = i; break; }
  }
  const text = (idx >= 0 ? history[idx].content : "").trim();
  if (!text) { const r = none(HELP); return { reply: r.reply, actions: [r.action] }; }
  try {
    const r = await serverChat(history.slice(0, idx), text, onChunk);
    const say = asStr(r.reply);
    const raw = Array.isArray(r.actions) && r.actions.length
      ? (r.actions as Array<Record<string, unknown>>)
      : (r.action != null ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
    if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
    const actions: ChatAction[] = [];
    for (const a of raw) {
      const one = await toAction({ action: a.name ?? a.action, params: a.params, reply: "" });
      actions.push(one.action);
    }
    return { reply: say, actions };
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      const r = none(e.message);
      return { reply: r.reply, actions: [r.action] };
    }
    // Free offline / transient fallback: the original on-device regex router below.
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ── LOCAL FALLBACK (the original regex router — free, on-device, kept intact) ────

const SUMMARIZE_RE = /\b(summar(?:y|ise|ize)|recap|overview|weekly|this\s+week|how(?:'s| is| has| have| did| was)\b.*\b(week|focus|productiv))/i;

function parseMinutes(text: string): number | null {
  const m = text.match(/(\d+)\s*(?:min(?:ute)?s?|m)\b/i) || text.match(/(\d+)\s*(?:hour|hr|h)\b/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  const isHours = /(?:hour|hr|h)\b/i.test(m[0]) && !/m(?:in)?/i.test(m[0]);
  const mins = isHours ? n * 60 : n;
  return Math.max(1, Math.min(600, mins));
}

async function newestSession(): Promise<FocusSession | null> {
  const all = await store.listSessions(50000);
  if (all.length === 0) return null;
  return [...all].sort((a, b) => (a.started_at < b.started_at ? 1 : -1))[0];
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 0) DRAFT PLAN / REPEAT SESSION / GOALS — checked first so they never get misread as a filter/search.
  if (isDraftPlanRequest(t)) return { reply: "", action: { kind: "open_plan_flow" } };
  if (isRepeatSessionRequest(t)) return { reply: "", action: { kind: "repeat_session" } };
  const goalTitle = matchCreateGoal(t);
  if (goalTitle) return { reply: "", action: { kind: "create_goal", title: goalTitle } };
  const ideasFor = matchGoalIdeas(t);
  if (ideasFor) return { reply: "", action: { kind: "goal_ideas", goal_title: ideasFor } };

  // 0b) DELETE GOAL — "delete my Spanish goal" / "remove goal X" (UI confirms).
  const delGoalM = t.match(/^(?:delete|remove|drop)\s+(?:my\s+|the\s+)?(?:goal\s+(?:to\s+|of\s+|for\s+)?)?(.+?)\s+goal\s*$/i)
    || t.match(/^(?:delete|remove|drop)\s+(?:my\s+|the\s+)?goal\s+(?:to\s+|of\s+|for\s+|called\s+)?(.+)$/i);
  if (delGoalM) {
    const { goal, problem } = await resolveGoal(delGoalM[1].trim());
    if (!goal) return none(problem!);
    return { reply: "", action: { kind: "delete_goal", id: goal.id, label: `"${goal.title}" goal` } };
  }

  // 1) SET DEFAULT — settings update ("set default focus to 25 minutes").
  let m = t.match(/\bset\s+(?:my\s+)?default\s+(?:focus\s+|session\s+)?(?:length|duration|time|focus)?\s*(?:to\s+)?(\d+)\s*(?:min(?:ute)?s?|m)?\b/i);
  if (m) {
    const mins = Math.max(1, Math.min(600, parseInt(m[1], 10)));
    return { reply: "", action: { kind: "set_default", minutes: mins } };
  }

  // 2) ADD — log a focus session (leading verb + a duration).
  if (/^(?:log|add|record)\b/i.test(t)) {
    const mins = parseMinutes(t);
    if (!mins) return none('How long was it? Try "log a 25 minute focus session".');
    const refM = t.match(/\b(?:reflection|note|notes)\s*:?\s*(.+)$/i);
    return { reply: "", action: { kind: "add_session", duration_min: mins, reflection: refM ? refM[1].trim() : null } };
  }

  // 3) DELETE — "delete my last / latest / most recent session" (UI confirms).
  if (/^(?:delete|remove)\b/i.test(t)) {
    if (/\b(last|latest|most\s+recent|recent|previous|session)\b/i.test(low)) {
      const s = await newestSession();
      if (!s) return none("There are no sessions to delete.");
      return { reply: "", action: { kind: "delete_session", id: s.id, label: `the ${s.duration_min}-minute session from ${fmtWhen(s.started_at)}` } };
    }
    return none('Tell me which one — try "delete my last session".');
  }

  // 4) FILTER / SEARCH — local, free listing.
  m = t.match(/^(?:show|list|find|view)\s+(.*)$/i);
  if (m) {
    const rest = m[1].toLowerCase();
    const completedOnly = /\bcompleted\b/.test(rest);
    let days: number | undefined;
    const lastN = rest.match(/last\s+(\d+)\s*days?/);
    if (lastN) days = parseInt(lastN[1], 10);
    else if (/\blast\s+week\b|\bthis\s+week\b/.test(rest)) days = 7;
    else if (/\blast\s+month\b/.test(rest)) days = 30;
    const label = days ? `last ${days} days${completedOnly ? ", completed" : ""}` : completedOnly ? "completed" : "recent sessions";
    return { reply: "", action: { kind: "filter", days, completedOnly, label } };
  }

  // 5) SUMMARIZE — weekly AI summary via the existing endpoint (billed).
  if (SUMMARIZE_RE.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // 6) Unclear → a short clarifying nudge (no billed call).
  return none(HELP);
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
